import { useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Loader2, Check, ArrowLeft, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import useAvailability, {
  AVAILABILITY_MODIFIER_CLASSES,
  STATUS_DOT_CLASS,
  STATUS_LABELS,
  STATUS_ORDER,
} from "@/lib/useAvailability";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANLAESSE = [
  "Dinner für zwei",
  "Geburtstag",
  "Jubiläum / Hochzeitstag",
  "Dinner mit Freunden",
  "Business-Menü zuhause",
  "Weihnachten / Silvester",
  "Anderer Anlass",
];

const GAENGE_OPTIONS = [3, 4, 5, 6, 7];

const KUECHENSTIL_OPTIONS = [
  { key: "fine-dining", label: "Fine-Dining" },
  { key: "mediterran", label: "Mediterran" },
  { key: "regional", label: "Regional / Saisonal" },
  { key: "vegetarisch", label: "Vegetarisch-Fokus" },
  { key: "vegan", label: "Vegan" },
  { key: "kindgerecht", label: "Kindgerecht" },
  { key: "ueberrasch", label: "Überrasch mich" },
];

const ALLERGIE_OPTIONS = [
  { key: "glutenfrei", label: "Glutenfrei" },
  { key: "laktosefrei", label: "Laktosefrei" },
  { key: "nuesse", label: "Nüsse" },
  { key: "meeresfruechte", label: "Meeresfrüchte" },
  { key: "keine", label: "Keine" },
];

const BUDGET_OPTIONS = [
  { key: "bis-40", label: "bis 40 €" },
  { key: "40-70", label: "40–70 €" },
  { key: "70-100", label: "70–100 €" },
  { key: "100-plus", label: "100 € +" },
  { key: "offen", label: "Sag mir was möglich ist" },
];

const KUECHE_OPTIONS = [
  { key: "voll", label: "Vollausgestattet" },
  { key: "klein", label: "Eher klein / einfach" },
  { key: "unbekannt", label: "Weiß ich nicht — bitte anrufen" },
];

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#E3DECE] rounded-none focus:outline-none focus:border-[#BA6D52] focus:ring-0 px-0 py-3 text-[#2C2A26] placeholder:text-[#A8A399] text-base font-light transition-colors";

export default function AnfragePrivat() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    einsatzdatum: "",
    von_uhrzeit: "",
    bis_uhrzeit: "",
    anlass: "",
    anzahl_gaeste: 6,
    anzahl_gaenge: 5,
    kuechenstil: [],
    allergien: [],
    allergien_freitext: "",
    budget_pro_person: "",
    kueche_vor_ort: "",
    name: "",
    ort: "",
    telefon: "",
    email: "",
    nachricht: "",
    agb_accepted: false,
    widerruf_verzicht: false,
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const { statusOf, byStatus, loading: availLoading } = useAvailability();

  // Datum als Date-Objekt (fuer Calendar-Komponente)
  const selectedDate = useMemo(() => {
    if (!form.einsatzdatum) return undefined;
    const [y, m, d] = form.einsatzdatum.split("-").map(Number);
    if (!y || !m || !d) return undefined;
    return new Date(y, m - 1, d);
  }, [form.einsatzdatum]);

  // Kalender-Modifier: farbige Zellen fuer available/request/booked/unavailable
  const isDayDisabled = useCallback(
    (d) => {
      const past = d < new Date(new Date().setHours(0, 0, 0, 0));
      const s = statusOf(d);
      return past || s === "booked" || s === "unavailable";
    },
    [statusOf],
  );
  const calendarModifiers = useMemo(
    () => ({
      ...byStatus,
      defaultAvailable: (d) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return d >= today && !statusOf(d);
      },
    }),
    [byStatus, statusOf],
  );
  const calendarModifierClasses = useMemo(
    () => ({
      ...AVAILABILITY_MODIFIER_CLASSES,
      defaultAvailable: AVAILABILITY_MODIFIER_CLASSES.available,
    }),
    [],
  );

  const dateLabel = useMemo(() => {
    if (!selectedDate) return "Datum auswählen";
    return format(selectedDate, "EEEE, dd. MMMM yyyy", { locale: de });
  }, [selectedDate]);

  // Live-Status fuer das gewaehlte Datum (verfuegbar / auf Anfrage / ausgebucht / nicht verfuegbar)
  const dateStatus = (() => {
    if (!selectedDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) return "past";
    return statusOf(selectedDate) || "available";
  })();

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInArray = (k, v) => setForm((f) => ({
    ...f,
    [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
  }));

  // Prüft, ob der Einsatz innerhalb der 14-Tage-Widerrufsfrist liegt
  const isWithinWiderrufFrist = () => {
    if (!form.einsatzdatum) return false;
    const einsatz = new Date(form.einsatzdatum);
    const frist = new Date();
    frist.setDate(frist.getDate() + 14);
    return einsatz <= frist;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agb_accepted) {
      toast.error("Bitte AGB & Datenschutz zustimmen");
      return;
    }
    if (isWithinWiderrufFrist() && !form.widerruf_verzicht) {
      toast.error("Bitte bestätigen Sie den Verzicht auf das Widerrufsrecht für den kurzfristigen Einsatz");
      return;
    }
    if (!form.einsatzdatum || !form.name || !form.ort || !form.email || !form.anlass) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        einsatzdatum: form.einsatzdatum,
        von_uhrzeit: form.von_uhrzeit || "",
        bis_uhrzeit: form.bis_uhrzeit || "",
        time_flexibility: form.von_uhrzeit ? "fest" : "absprache",
        ort: form.ort,
        betrieb: form.name, // Bei Privat = Name des Kunden
        einsatzdauer: "privat_event",
        anzahl_tage: 1,
        bereich: "Privat-Event",
        telefon: form.telefon || "",
        email: form.email,
        nachricht: form.nachricht || "",
        service_type: "private",
        anlass: form.anlass,
        anzahl_gaeste: Number(form.anzahl_gaeste) || 0,
        anzahl_gaenge: Number(form.anzahl_gaenge) || 0,
        kuechenstil: form.kuechenstil || [],
        allergien: form.allergien || [],
        allergien_freitext: form.allergien_freitext || "",
        budget_pro_person: form.budget_pro_person || "",
        kueche_vor_ort: form.kueche_vor_ort || "",
        agb_accepted: true,
        agb_accepted_at: new Date().toISOString(),
      });
      setDone(true);
      toast.success("Anfrage gesendet — ich melde mich innerhalb von 24 Stunden.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="page-anfrage-privat-done">
        <section className="border-b border-[#E3DECE]">
          <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-[#BA6D52] rounded-full">
              <Check size={32} className="text-[#BA6D52]" strokeWidth={1.5} />
            </div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52] mt-8">Vielen Dank</p>
            <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
              Ihre Menü-Anfrage ist angekommen.
            </h1>
            <p className="text-[#757067] font-light text-lg mt-8 leading-relaxed">
              Ich melde mich innerhalb von 24 Stunden bei Ihnen — für ein
              persönliches Vorgespräch zu Ihrem Anlass und den Menüwünschen.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate("/")}
                className="border border-[#2C2A26] text-[#2C2A26] hover:bg-[#2C2A26] hover:text-white px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Zurück zur Startseite
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="page-anfrage-privat">
      <section className="border-b border-[#E3DECE]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link
            to="/privatkoch"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#757067] hover:text-[#BA6D52]"
          >
            <ArrowLeft size={14} /> Zurück zu Privatkoch
          </Link>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52] mt-10">Menü-Beratung anfragen</p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter mt-6">
            Ihr Abend beginnt hier.
          </h1>
          <p className="text-[#757067] font-light text-lg mt-8 leading-relaxed max-w-2xl">
            Erzählen Sie mir von Ihrem Anlass. Nach unserem persönlichen Gespräch
            erhalten Sie ein individuelles Angebot mit allen Positionen —
            Menü, Anfahrt und Service.
          </p>
          <div className="mt-8 max-w-2xl border-l-2 border-[#BA6D52] pl-5 py-1" data-testid="anfrage-privat-hinweis">
            <p className="text-sm text-[#2C2A26] font-light leading-relaxed">
              <strong className="font-normal">Hinweis:</strong> Ich koche <em>Menü-Abende</em> bei Ihnen zuhause —
              frisch in Ihrer Küche gekocht und am gedeckten Tisch serviert.
              Kein Buffet, kein Brunch mit Warmhaltebehältern, kein mobiles Catering.
              Ab 2 Personen — größere Runden je nach Location auf Anfrage.
            </p>
          </div>
        </div>
      </section>

      <section>
        <form
          onSubmit={submit}
          className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20 space-y-14"
          data-testid="anfrage-privat-form"
        >
          {/* Anlass */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">01 · Anlass</p>
            <label className="block mt-8 text-sm text-[#757067] font-light">Um was für einen Abend geht es? *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
              {ANLAESSE.map((a) => (
                <button
                  type="button"
                  key={a}
                  onClick={() => setField("anlass", a)}
                  data-testid={`anlass-${a.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`border py-3 px-4 text-sm text-left transition-colors ${
                    form.anlass === a
                      ? "border-[#BA6D52] bg-[#BA6D52] text-white"
                      : "border-[#E3DECE] hover:border-[#BA6D52] text-[#2C2A26]"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          {/* Datum & Zeit */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">02 · Datum &amp; Zeit</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
              <div>
                <label className="block text-sm text-[#757067] font-light">Wunschdatum *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="anfrage-privat-datepicker"
                      className={`${inputCls} flex items-center justify-between text-left ${
                        !selectedDate ? "text-[#A8A399]" : ""
                      }`}
                    >
                      <span className="truncate">{dateLabel}</span>
                      <CalendarIcon size={18} className="text-[#BA6D52] flex-shrink-0 ml-2" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0 bg-[#FAFAF7] border-[#E3DECE] max-w-[calc(100vw-16px)]"
                    align="start"
                    collisionPadding={12}
                    sideOffset={8}
                  >
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => {
                        if (!d) return;
                        setField("einsatzdatum", format(d, "yyyy-MM-dd"));
                      }}
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      numberOfMonths={1}
                      locale={de}
                      modifiers={calendarModifiers}
                      modifiersClassNames={calendarModifierClasses}
                      disabled={isDayDisabled}
                      excludeDisabled
                      classNames={{
                        day_selected:
                          "!bg-[#BA6D52] !text-white !font-semibold hover:!bg-[#A05D45] rounded-md",
                      }}
                    />
                    <div className="border-t border-[#E3DECE] px-3 py-3 text-xs">
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                        {STATUS_ORDER.map((s) => (
                          <div key={s} className="flex items-center gap-1.5">
                            <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT_CLASS[s]}`} />
                            <span className="text-[#2C2A26] text-[11px] truncate">{STATUS_LABELS[s]}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                {selectedDate && !availLoading && dateStatus && (
                  <div
                    className="mt-3 flex items-center gap-2 text-xs"
                    data-testid="anfrage-privat-datum-status"
                  >
                    {dateStatus === "past" ? (
                      <>
                        <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 bg-[#2C2A26]" />
                        <span className="text-[#757067]">Datum liegt in der Vergangenheit</span>
                      </>
                    ) : (
                      <>
                        <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT_CLASS[dateStatus]}`} />
                        <span
                          className={
                            dateStatus === "available"
                              ? "text-emerald-700 font-medium"
                              : dateStatus === "request"
                              ? "text-yellow-700 font-medium"
                              : dateStatus === "booked"
                              ? "text-red-700 font-medium"
                              : "text-[#2C2A26] font-medium"
                          }
                        >
                          {STATUS_LABELS[dateStatus]}
                          {dateStatus === "request" && " — Anfrage möglich, ich melde mich"}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm text-[#757067] font-light">Ab (optional)</label>
                <input
                  type="time"
                  value={form.von_uhrzeit}
                  onChange={(e) => setField("von_uhrzeit", e.target.value)}
                  className={inputCls}
                  data-testid="anfrage-privat-von"
                />
              </div>
              <div>
                <label className="block text-sm text-[#757067] font-light">Bis (optional)</label>
                <input
                  type="time"
                  value={form.bis_uhrzeit}
                  onChange={(e) => setField("bis_uhrzeit", e.target.value)}
                  className={inputCls}
                  data-testid="anfrage-privat-bis"
                />
              </div>
            </div>
            <p className="text-xs text-[#757067] italic mt-3">Wenn keine Uhrzeit angegeben, klären wir das im Gespräch.</p>
          </div>

          {/* Gäste & Gänge */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">03 · Menü &amp; Gäste</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              <div>
                <label className="block text-sm text-[#757067] font-light">
                  Anzahl Gäste (ab 2) *
                </label>
                <input
                  type="number"
                  min="2"
                  required
                  value={form.anzahl_gaeste}
                  onChange={(e) => setField("anzahl_gaeste", e.target.value)}
                  className={inputCls}
                  data-testid="anfrage-privat-gaeste"
                />
              </div>
              <div>
                <label className="block text-sm text-[#757067] font-light">Gewünschte Gänge</label>
                <div className="flex gap-2 mt-4 flex-wrap">
                  {GAENGE_OPTIONS.map((g) => (
                    <button
                      type="button"
                      key={g}
                      onClick={() => setField("anzahl_gaenge", g)}
                      data-testid={`anfrage-privat-gaenge-${g}`}
                      className={`border w-12 h-12 text-sm font-light transition-colors ${
                        form.anzahl_gaenge === g
                          ? "border-[#BA6D52] bg-[#BA6D52] text-white"
                          : "border-[#E3DECE] hover:border-[#BA6D52] text-[#2C2A26]"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#757067] italic mt-3">3 bis 7 Gänge — kann im Gespräch angepasst werden.</p>
              </div>
            </div>
          </div>

          {/* Küchenstil */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Küchenstil — was passt zu Ihrem Abend?</p>
            <p className="text-xs text-[#757067] mt-2">Mehrfachauswahl möglich.</p>
            <div className="flex gap-2 mt-4 flex-wrap">
              {KUECHENSTIL_OPTIONS.map((o) => {
                const active = form.kuechenstil.includes(o.key);
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => toggleInArray("kuechenstil", o.key)}
                    data-testid={`anfrage-privat-stil-${o.key}`}
                    className={`px-4 py-2 border text-sm font-light transition-colors ${
                      active
                        ? "border-[#BA6D52] bg-[#BA6D52] text-white"
                        : "border-[#E3DECE] hover:border-[#BA6D52] text-[#2C2A26]"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Allergien */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Allergien & Unverträglichkeiten</p>
            <div className="flex gap-2 mt-4 flex-wrap">
              {ALLERGIE_OPTIONS.map((o) => {
                const active = form.allergien.includes(o.key);
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => toggleInArray("allergien", o.key)}
                    data-testid={`anfrage-privat-allergie-${o.key}`}
                    className={`px-4 py-2 border text-sm font-light transition-colors ${
                      active
                        ? "border-[#BA6D52] bg-[#BA6D52] text-white"
                        : "border-[#E3DECE] hover:border-[#BA6D52] text-[#2C2A26]"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            <textarea
              rows={2}
              value={form.allergien_freitext}
              onChange={(e) => setField("allergien_freitext", e.target.value)}
              placeholder="Weitere Allergien / Unverträglichkeiten der Gäste (z. B. Sellerie, Erdbeeren, Histamin) …"
              data-testid="anfrage-privat-allergien-freitext"
              className="mt-4 w-full bg-transparent border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-0 py-3 text-sm font-light"
            />
          </div>

          {/* Budget */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Budget-Rahmen pro Person</p>
            <p className="text-xs text-[#757067] mt-2">Inkl. Lebensmittel + Zubereitung + Anfahrt — orientierend, wird gemeinsam kalkuliert.</p>
            <div className="flex gap-2 mt-4 flex-wrap">
              {BUDGET_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setField("budget_pro_person", o.key)}
                  data-testid={`anfrage-privat-budget-${o.key}`}
                  className={`px-4 py-2 border text-sm font-light transition-colors ${
                    form.budget_pro_person === o.key
                      ? "border-[#BA6D52] bg-[#BA6D52] text-white"
                      : "border-[#E3DECE] hover:border-[#BA6D52] text-[#2C2A26]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Küche vor Ort */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Wie ist die Küche bei Ihnen ausgestattet?</p>
            <div className="flex gap-2 mt-4 flex-wrap">
              {KUECHE_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setField("kueche_vor_ort", o.key)}
                  data-testid={`anfrage-privat-kueche-${o.key}`}
                  className={`px-4 py-2 border text-sm font-light transition-colors ${
                    form.kueche_vor_ort === o.key
                      ? "border-[#BA6D52] bg-[#BA6D52] text-white"
                      : "border-[#E3DECE] hover:border-[#BA6D52] text-[#2C2A26]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Kontakt & Adresse */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">04 · Kontakt &amp; Adresse</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              <div>
                <label className="block text-sm text-[#757067] font-light">Ihr Name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className={inputCls}
                  data-testid="anfrage-privat-name"
                  placeholder="Vor- und Nachname"
                />
              </div>
              <div>
                <label className="block text-sm text-[#757067] font-light">Telefon</label>
                <input
                  type="tel"
                  value={form.telefon}
                  onChange={(e) => setField("telefon", e.target.value)}
                  className={inputCls}
                  data-testid="anfrage-privat-tel"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-[#757067] font-light">E-Mail *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  className={inputCls}
                  data-testid="anfrage-privat-email"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-[#757067] font-light">Adresse des Events *</label>
                <input
                  type="text"
                  required
                  value={form.ort}
                  onChange={(e) => setField("ort", e.target.value)}
                  className={inputCls}
                  data-testid="anfrage-privat-ort"
                  placeholder="Straße Nr., PLZ Ort"
                />
              </div>
            </div>
          </div>

          {/* Wünsche */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">05 · Ihre Wünsche</p>
            <label className="block mt-8 text-sm text-[#757067] font-light">
              Was ist Ihnen wichtig? Vorlieben, Diäten, Allergien, besondere Ideen?
            </label>
            <textarea
              rows={5}
              value={form.nachricht}
              onChange={(e) => setField("nachricht", e.target.value)}
              placeholder="z.B. vegetarisch, mediterran, überraschender Gruß aus der Küche, keine Meeresfrüchte, saisonale Zutaten bevorzugt ..."
              className="w-full mt-2 bg-transparent border-b border-[#E3DECE] rounded-none focus:outline-none focus:border-[#BA6D52] px-0 py-3 text-[#2C2A26] placeholder:text-[#A8A399] text-base font-light resize-none"
              data-testid="anfrage-privat-nachricht"
            />
          </div>

          {/* AGB */}
          <div className="border-t border-[#E3DECE] pt-8 space-y-4">
            <div className="p-4 bg-[#FAF5F1] border-l-4 border-[#BA6D52] text-sm font-light leading-relaxed text-[#2C2A26]"
              data-testid="anfrage-privat-storno-hinweis">
              <b className="text-[#BA6D52]">Ihr Termin wird exklusiv reserviert.</b>{" "}
              Ich koche an einem Abend nur für eine Gesellschaft — Ihr Termin wird daher fest für
              Sie geblockt. Eine Absage ist bis <b>22&nbsp;Tage</b> vor dem Termin kostenfrei.
              Danach gilt eine gestaffelte Ausfallpauschale (30&nbsp;% / 50&nbsp;% / 70&nbsp;% /
              90&nbsp;% je nach Zeitpunkt), wobei ein geringerer Schaden immer nachgewiesen werden
              darf. Eine einmalige Terminverschiebung ist bis 21&nbsp;Tage vorher kostenfrei —
              Details in Ziffer&nbsp;4.8 der{" "}
              <Link to="/agb" target="_blank" className="text-[#BA6D52] underline">AGB</Link>.
            </div>
            <label className="flex items-start gap-3 cursor-pointer text-sm font-light text-[#2C2A26]">
              <input
                type="checkbox"
                checked={form.agb_accepted}
                onChange={(e) => setField("agb_accepted", e.target.checked)}
                className="mt-1 accent-[#BA6D52]"
                data-testid="anfrage-privat-agb"
              />
              <span>
                Ich stimme den{" "}
                <Link to="/agb" target="_blank" className="text-[#BA6D52] underline">AGB</Link> und der
                {" "}<Link to="/datenschutz" target="_blank" className="text-[#BA6D52] underline">Datenschutzerklärung</Link>
                {" "}zu. Meine Kontaktdaten werden ausschließlich zur Bearbeitung dieser Anfrage
                verwendet (Art. 6 Abs. 1 lit. b DSGVO). *
              </span>
            </label>

            {isWithinWiderrufFrist() && (
              <label className="flex items-start gap-3 cursor-pointer text-sm font-light text-[#2C2A26] p-4 bg-[#FAF5F1] border-l-4 border-[#BA6D52]">
                <input
                  type="checkbox"
                  checked={form.widerruf_verzicht}
                  onChange={(e) => setField("widerruf_verzicht", e.target.checked)}
                  className="mt-1 accent-[#BA6D52]"
                  data-testid="anfrage-privat-widerruf"
                />
                <span>
                  <b className="text-[#BA6D52]">Kurzfristiger Einsatz — Widerrufsrecht:</b>{" "}
                  Da der Einsatz innerhalb der 14-tägigen Widerrufsfrist stattfindet, stimme
                  ich ausdrücklich zu, dass mit der Leistungserbringung vor Ablauf der
                  Widerrufsfrist begonnen wird. Ich habe zur Kenntnis genommen, dass mein
                  Widerrufsrecht bei vollständiger Erbringung der Leistung durch den
                  Auftragnehmer erlischt (siehe §&nbsp;12 der{" "}
                  <Link to="/agb" target="_blank" className="text-[#BA6D52] underline">AGB</Link>). *
                </span>
              </label>
            )}
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <button
              type="submit"
              disabled={sending}
              data-testid="anfrage-privat-submit"
              className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-10 py-5 text-xs uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-3"
            >
              {sending ? <><Loader2 size={16} className="animate-spin" /> Wird gesendet …</> : "Anfrage senden"}
            </button>
            <p className="text-xs text-[#757067] font-light">
              Antwort innerhalb von 24 Stunden — kostenlos und unverbindlich.
            </p>
          </div>
        </form>
      </section>
    </div>
  );
}
